import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtMoney } from "@/lib/format";
import { ArrowClockwise, CheckCircle, ArrowFatRight } from "@phosphor-icons/react";

/**
 * Customer Advance Orders — flat list of open parts lines earmarked
 * for customers via Direct-Parts / Accident-Engine service advances.
 * Powers the "which parts do I have to hold for customers?" question
 * on the Required Order page.
 *
 * Status pills:
 *   ordered   — collected advance, part not yet received from vendor
 *   received  — part arrived at branch, awaiting customer pickup / JC issue
 *   (delivered lines are hidden by default)
 */
const STATUS_META = {
  ordered: { label: "Ordered", cls: "border-ink text-ink" },
  received: { label: "Received", cls: "border-[#00713a] text-[#00713a]" },
  delivered: { label: "Delivered", cls: "border-canvas-line text-ink-subtle" },
};

export default function CustomerAdvanceOrders({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(""); // ""=open (ordered+received); "delivered"=history
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      const { data } = await api.get("/service-advances/parts/open", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const advance = async (row, next_status) => {
    setBusyId(row.id);
    try {
      await api.patch(`/service-advances/${row.advance_id}/parts/${row.id}`, { status: next_status });
      toast.success(`Line marked ${next_status}`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusyId(""); }
  };

  return (
    <div className="border border-canvas-line bg-canvas-card">
      <div className="px-3 py-2 border-b border-canvas-line bg-canvas-panel flex items-center gap-2">
        <div className="font-display font-bold text-sm">Customer Advance Orders</div>
        <div className="text-[11px] text-ink-muted font-mono uppercase tracking-wider">
          · Parts held for customers via Direct-Parts / Accident-Engine advances
        </div>
        <div className="ml-auto inline-flex border border-canvas-line" data-testid="cao-status-tabs">
          {[["", "Open"], ["ordered", "Ordered"], ["received", "Received"], ["delivered", "History"]].map(([v, l], i) => (
            <button key={v || "open"} type="button" onClick={() => setStatus(v)}
              data-testid={`cao-status-${v || "open"}`}
              className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider ${i > 0 ? "border-l border-canvas-line" : ""} ${status === v ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}>
              {l}
            </button>
          ))}
        </div>
        <button type="button" onClick={load}
          data-testid="cao-refresh"
          className="inline-flex items-center gap-1 px-2 py-1 border border-canvas-line hover:border-ink text-[10px] font-mono uppercase">
          <ArrowClockwise size={10} weight="bold" /> Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cao-table">
          <thead className="bg-canvas-panel/50">
            <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
              <th className="px-3 py-2">Advance #</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Customer / Vehicle</th>
              <th className="px-3 py-2">Part</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit ₹</th>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Expected</th>
              <th className="px-3 py-2">JC #</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 w-40"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-3 py-6 text-center text-ink-subtle font-mono text-xs">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-ink-subtle font-mono text-xs">
                No {status || "open"} customer advance parts.
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const meta = STATUS_META[r.status] || {};
              const otype = r.order_type;
              return (
                <tr key={r.id} data-testid={`cao-row-${r.id}`} className="border-t border-canvas-line hover:bg-canvas-panel/40">
                  <td className="px-3 py-2 font-mono text-xs">{r.advance_no}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.customer_name || "—"}</div>
                    <div className="text-xs text-ink-muted">{r.vehicle_reg_no || ""}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs">{r.part_code}</div>
                    <div className="text-[10px] text-ink-muted">{r.description || ""}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{r.qty}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{fmtMoney(r.unit_price)}</td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-[11px]">
                      <span className={`inline-block px-1 py-0 border mr-1 text-[10px] ${otype === "VOR" ? "border-[#c92a2a] text-[#c92a2a]" : otype === "APS" ? "border-[#00713a] text-[#00713a]" : otype ? "border-ink text-ink" : "border-canvas-line text-ink-subtle"}`}>
                        {otype || "—"}
                      </span>
                      {r.order_number || <span className="text-ink-subtle">—</span>}
                    </div>
                    <div className="text-[10px] text-ink-muted">{r.order_date || ""}</div>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-ink-muted">{r.expected_delivery_date || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.jc_no || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${meta.cls || ""}`}>
                      {meta.label || r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {r.status === "ordered" && (
                      <button type="button" onClick={() => advance(r, "received")} disabled={busyId === r.id}
                        data-testid={`cao-receive-${r.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 border border-[#00713a] text-[#00713a] text-[10px] font-mono uppercase hover:bg-[#00713a] hover:text-white disabled:opacity-50">
                        <ArrowFatRight size={10} weight="bold" /> Mark received
                      </button>
                    )}
                    {r.status === "received" && (
                      <button type="button" onClick={() => advance(r, "delivered")} disabled={busyId === r.id}
                        data-testid={`cao-deliver-${r.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 border border-ink text-ink text-[10px] font-mono uppercase hover:bg-ink hover:text-white disabled:opacity-50">
                        <CheckCircle size={10} weight="bold" /> Delivered
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
